import { Button, Card, Progress, Spin, Table } from "antd";
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import SupervisionService from "../../../../../../services/SupervisionService";
import { SupervisionHistory } from "../../../../../../models/Supervision";
import { CaretLeftOutlined, EyeTwoTone } from "@ant-design/icons";
import UserContext from "../../../../../../context/UserContext";
import { useLanguage } from "../../../../../../context/LanguageContext";
import { getWarehouseReferenceColor } from "../../../../../../common/warehouseColorReferences";

const SupervisionsHistory: React.FC = () => {
  const [supervisionSweeps, setSupervisionSweeps] = useState<SupervisionHistory[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const { language } = useLanguage();
  const { warehouseIdx } = useParams();
  const isMobile = useContext(UserContext);
  const progressPercentTextStyle: React.CSSProperties = {
    color: "#686565",
    fontSize: "12px",
    fontWeight: 500,
  };
  const getEffectiveColor = (effectivePercentage: number): string => {
    if (effectivePercentage > 0.75) {
      return "#1677ff";
    }

    if (effectivePercentage > 0.5) {
      return "#ebd230";
    }

    return "#f5222d";
  };

  const updateContent = useCallback(() => {
  }, []);
  const navigate = useNavigate();

  const columns = useMemo(
    () => [
      {
        title: language === "es" ? "Depósito" : "Warehouse",
        dataIndex: "warehouse_name",
        key: "warehouse_name",
        width: "16%",
        render: (value: SupervisionHistory["warehouse_name"]) => {
          const color = getWarehouseReferenceColor(value);
          return (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
              <span
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  backgroundColor: color,
                  display: "inline-block",
                }}
              />
              <span style={{ color, fontWeight: 600 }}>{value}</span>
            </span>
          );
        },
      },
      {
        title: language === "es" ? "Número de Conteo" : "Counting Number",
        dataIndex: "counting_number",
        key: "counting_number",
        align: "center" as const,
        width: "12%",
        render: (value: SupervisionHistory["counting_number"]) => {
          if (value === null || value === undefined) {
            return "-";
          }

          return String(value).trim() === "" ? "-" : value;
        },
      },
      {
        title: language === "es" ? "Cantidad de Dispositivos" : "Devices Counting",
        dataIndex: "devices_count",
        key: "devices_count",
        align: "center" as const,
        width: "12%",
      },
      {
        title: language === "es" ? "Lecturas" : "Readings",
        key: "readings",
        width: "24%",
        render: (_: unknown, record: SupervisionHistory) =>
          record.is_processing ? (
            "-"
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
              <Progress
                type="line"
                percent={parseFloat((record.reading_percentage * 100).toFixed(2))}
                format={(percent) => (
                  <span style={progressPercentTextStyle}>{`${percent}%`}</span>
                )}
                status="active"
                strokeWidth={10}
                style={{ width: "300px", margin: 0 }}
              />
              <div style={{ fontSize: "13px" }}>
                <span style={{ color: "#1677ff", fontWeight: 600 }}>
                  {record.read_positions}
                </span>
                <span style={{ color: "#8c8c8c", margin: "0 4px" }}>/</span>
                <span style={{ color: "#8c8c8c", fontWeight: 400 }}>
                  {record.total_count}
                </span>
              </div>
            </div>
          ),
      },
      {
        title: language === "es" ? "Efectividad" : "Effective",
        key: "effective",
        width: "24%",
        render: (_: unknown, record: SupervisionHistory) =>
          record.is_processing ? (
            "-"
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
              <Progress
                type="line"
                percent={parseFloat((record.effective_percentage * 100).toFixed(2))}
                format={(percent) => (
                  <span style={progressPercentTextStyle}>{`${percent}%`}</span>
                )}
                status="active"
                strokeWidth={10}
                style={{ width: "300px", margin: 0 }}
                strokeColor={getEffectiveColor(record.effective_percentage)}
              />
              <div style={{ fontSize: "13px" }}>
                <span
                  style={{
                    color: getEffectiveColor(record.effective_percentage),
                    fontWeight: 600,
                  }}
                >
                  {record.effective_readings}
                </span>
                <span style={{ color: "#8c8c8c", margin: "0 4px" }}>/</span>
                <span style={{ color: "#8c8c8c", fontWeight: 400 }}>
                  {record.read_positions}
                </span>
              </div>
            </div>
          ),
      },
      {
        title: language === "es" ? "Detalles" : "Details",
        key: "details",
        align: "center" as const,
        width: "8%",
        render: (_: unknown, record: SupervisionHistory) =>
          record.is_processing ? (
            <Button type="link" icon={<EyeTwoTone />} disabled />
          ) : (
            <Link to={`/supervision/history/${record.warehouse_idx}/${record.sweep_idx}`}>
              <Button type="link" icon={<EyeTwoTone />} />
            </Link>
          ),
      },
    ],
    [language, progressPercentTextStyle]
  );

  useEffect(() => {
    SupervisionService.getSupervisionHistories(Number(warehouseIdx), navigate)
      .then((supervisionSweep) => {
        setSupervisionSweeps(supervisionSweep || []);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Failed to fetch sweeps:", error);
        setLoading(false);
      });
  }, [warehouseIdx, navigate]);

  useEffect(() => {
    updateContent();
    const interval = setInterval(() => {
      updateContent();
    }, 180000);
    return () => clearInterval(interval);
  }, [updateContent]);

  return (
    <>
      <Card
        style={{
          margin: isMobile ? "10px" : "20px",
          padding: isMobile ? "10px" : "20px",
        }}
        title={language === "es" ? "Historial de Supervisiones" : "Supervision History"}
        extra={<Link to={`/supervision`} >
          <Button
            type="primary"
            shape="round"
            icon={<CaretLeftOutlined />}
          >
            {language === "es" ? "Atrás" : "Back"}
          </Button>
        </Link>}
      >
        <Spin spinning={loading}>
          <Table
            columns={columns}
            dataSource={supervisionSweeps}
            tableLayout="fixed"
            rowKey="sweep_idx"
            pagination={false}
            locale={{
              emptyText:
                language === "es"
                  ? "Historial no disponible"
                  : "No History Available",
            }}
          />
        </Spin>
      </Card >
    </>
  );
};

export default SupervisionsHistory;
